import { activateLoading, catchApiError, Loading, PageableListDto, SnackbarService } from '@fleet-beat/core';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { DataSourceParams } from '../models/data-source-params.model';
import { PageableDataSource } from '../models/pageable-data-source.model';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export abstract class BaseDataSourceService<Data, Params extends DataSourceParams<any, any>> extends OnDestroyMixin {
  protected constructor(protected snackbarService: SnackbarService) {
    super();
  }

  getPageableDataSource(params?: Params): PageableDataSource<Observable<PageableListDto<Data>>, Params> {
    const reloadSubject: BehaviorSubject<Params | undefined> = new BehaviorSubject(params);
    const loading: Loading = new Loading();
    const params$: Observable<Params> = reloadSubject.pipe(
      filter(Boolean),
      map((nextParams: Params) => (params = this.mergeDataSourceParams(params, nextParams))),
    );

    const dataSource$: Observable<PageableListDto<Data>> = params$.pipe(
      switchMap((nextParams: Params) =>
        this.getData(nextParams).pipe(activateLoading(loading), catchApiError(this.snackbarService)),
      ),
    );

    return {
      reloadSubject,
      loading: loading,
      dataSource: dataSource$,
      params$,
      getById: (id: string) => this.getById(id),
      getData: (params: Params) => this.getData(params),
    };
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  protected getById(id: string): Observable<Data> {
    throw new Error('Not Implemented');
  }

  protected abstract getData(query: Params): Observable<PageableListDto<Data>>;

  private mergeDataSourceParams(prevParams?: Params, currParams?: Params): Params {
    return {
      params: {
        ...prevParams?.params,
        ...currParams?.params,
      },
      extra: {
        ...prevParams?.extra,
        ...currParams?.extra,
      },
    } as Params;
  }
}
